import React from 'react';
import {
  Box, Stack, Typography, Select, MenuItem, ListSubheader, Tooltip,
} from '@mui/material';
import { Info } from 'lucide-react';
import { ZMAN_DEFINITIONS, DAY_KEY_MAP, DIRECT_TO_DAY_MAP, GROUP_ORDER } from './zmanDefinitions';

const selectSx = {
  fontSize: '0.8rem',
  bgcolor: 'white',
  '& .MuiOutlinedInput-notchedOutline': { borderColor: '#e5e7eb' },
};

/**
 * ZmanSelect — רכיב dropdown מאוחד לבחירת זמן הלכתי
 *
 * Props:
 *  value            string  — המפתח הנבחר
 *  onChange         (key) => void
 *  keyPrefix        '' | 'day' — '' = מפתחות ישירים; 'day' = מפתחות day* (PrayerListStep)
 *  showSentinels    bool   — הצג sentinel keys בראש (dayEntryTime, dayExitTime, etc.)
 *  sentinelLabels   { entry?: string, exit?: string } — תוויות override ל-sentinel keys
 *  extraOptions     [{key, label, time?}] — אפשרויות נוספות ייחודיות לתאריך (candleLighting, etc.)
 *  hiddenDirectKeys string[] — מפתחות ישירים להסתיר מהרשימה הסטנדרטית
 *  showNone         bool   — הצג "ללא — אל תציג זמן זה"
 *  hebrewDay        number — לסינון showOnlyWhen (זמני פסח)
 *  hebrewMonth      number
 *  zmanimForDay     object — { key: 'HH:MM', ... } — זמנים ליצוגית ליד כל אפשרות
 *                            null = אל תציג שעות
 *  size             'small' | 'medium'
 *  sx               object — sx נוסף לבורר
 *  disabled         bool
 *  MenuProps        object
 */
const ZmanSelect = ({
  value,
  onChange,
  keyPrefix = '',
  showSentinels = false,
  sentinelLabels = null,
  extraOptions = [],
  hiddenDirectKeys = [],
  showNone = false,
  hebrewDay = null,
  hebrewMonth = null,
  zmanimForDay = null,
  size = 'small',
  sx = {},
  disabled = false,
  contextLabel = null,
  MenuProps: menuPropsProp = {},
}) => {
  const hiddenSet = new Set(hiddenDirectKeys);

  // Build ordered groups from ZMAN_DEFINITIONS
  const groupMap = {};
  ZMAN_DEFINITIONS.forEach(def => {
    if (hiddenSet.has(def.key)) return;
    if (def.showOnlyWhen) {
      if (def.showOnlyWhen.hebrewDay !== hebrewDay || def.showOnlyWhen.hebrewMonth !== hebrewMonth) return;
    }
    const g = def.group || '';
    if (!groupMap[g]) groupMap[g] = [];
    groupMap[g].push(def);
  });
  const orderedGroups = GROUP_ORDER
    .filter(g => groupMap[g]?.length)
    .map(g => ({ header: g, defs: groupMap[g] }));

  // Find the direct key for tooltip lookup
  const directKey = keyPrefix === 'day' ? (DAY_KEY_MAP[value] ?? value) : value;
  const currentDef = ZMAN_DEFINITIONS.find(d => d.key === directKey);

  const tooltipContent = currentDef ? (
    <Box sx={{ maxWidth: 280, p: 0.5 }}>
      <Typography sx={{ fontWeight: 700, fontSize: '0.82rem', mb: 0.5 }}>{currentDef.label}</Typography>
      <Typography sx={{ fontSize: '0.73rem', color: '#9ca3af', mb: 0.75 }}>שיטה: {currentDef.method}</Typography>
      <Typography sx={{ fontSize: '0.77rem', lineHeight: 1.65 }}>{currentDef.tooltip}</Typography>
      <Typography sx={{ fontSize: '0.68rem', color: '#6b7280', mt: 1, pt: 0.75, borderTop: '1px solid #4b5563' }}>
        מקור: Hebcal API · דיוק שניות (sec=1) · קואורדינטות מיקום הארגון
      </Typography>
    </Box>
  ) : null;

  // Helper: render a MenuItem with optional time on the right
  const renderOption = (key, menuKey, label, fontWeight = 400) => {
    const time = zmanimForDay?.[key];
    return (
      <MenuItem key={menuKey} value={menuKey} sx={{ fontSize: '0.8rem', fontWeight, minWidth: 0 }}>
        {time ? (
          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ width: '100%' }} spacing={1}>
            <span style={{ flexShrink: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {label}
            </span>
            <Typography sx={{ fontSize: '0.72rem', color: '#9ca3af', flexShrink: 0, fontWeight: 400 }}>
              {time}
            </Typography>
          </Stack>
        ) : label}
      </MenuItem>
    );
  };

  return (
    <Stack direction="row" alignItems="center" spacing={0.5} sx={{ flex: 1, minWidth: 0 }}>
      <Select
        size={size}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
        sx={{ ...selectSx, flex: 1, minWidth: 0, ...sx }}
        MenuProps={{ PaperProps: { sx: { maxHeight: 400 } }, ...menuPropsProp }}
      >
        {contextLabel && (
          <ListSubheader key="__context_date" sx={{ fontSize: '0.7rem', color: '#0c4a6e', bgcolor: '#e0f2fe', lineHeight: '26px', fontWeight: 700 }}>
            📅 זמנים ל: {contextLabel}
          </ListSubheader>
        )}
        {/* Sentinel keys (dayEntryTime, dayExitTime, etc.) */}
        {showSentinels && [
          <ListSubheader key="__sentinel_hdr" sx={{ fontSize: '0.68rem', lineHeight: '26px', color: '#9ca3af' }}>
            בהתאם להגדרת כניסה/יציאה גלובלית
          </ListSubheader>,
          <MenuItem key="dayEntryTime" value="dayEntryTime" sx={{ fontSize: '0.8rem', minWidth: 0 }}>
            {zmanimForDay?.dayEntryTime ? (
              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ width: '100%' }} spacing={1}>
                <span>{sentinelLabels?.entry || 'לפי הגדרת כניסה (גלובלית)'}</span>
                <Typography sx={{ fontSize: '0.72rem', color: '#9ca3af', flexShrink: 0, fontWeight: 400 }}>{zmanimForDay.dayEntryTime}</Typography>
              </Stack>
            ) : (sentinelLabels?.entry || 'לפי הגדרת כניסה (גלובלית)')}
          </MenuItem>,
          <MenuItem key="dayExitTime" value="dayExitTime" sx={{ fontSize: '0.8rem', minWidth: 0 }}>
            {(() => { const t = zmanimForDay?.dayExitTime || zmanimForDay?.tzeit85deg; return t ? (
              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ width: '100%' }} spacing={1}>
                <span>{sentinelLabels?.exit || 'לפי הגדרת יציאה (גלובלית)'}</span>
                <Typography sx={{ fontSize: '0.72rem', color: '#9ca3af', flexShrink: 0, fontWeight: 400 }}>{t}</Typography>
              </Stack>
            ) : (sentinelLabels?.exit || 'לפי הגדרת יציאה (גלובלית)'); })()}
          </MenuItem>,
          <MenuItem key="dayEntrySunset" value="dayEntrySunset" sx={{ fontSize: '0.8rem', minWidth: 0 }}>
            {(() => { const t = zmanimForDay?.dayEntrySunset || zmanimForDay?.eveSunset; return t ? (
              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ width: '100%' }} spacing={1}>
                <span>שקיעה בערב המועד</span>
                <Typography sx={{ fontSize: '0.72rem', color: '#9ca3af', flexShrink: 0, fontWeight: 400 }}>{t}</Typography>
              </Stack>
            ) : 'שקיעה בערב המועד'; })()}
          </MenuItem>,
          <MenuItem key="dayExitSunset" value="dayExitSunset" sx={{ fontSize: '0.8rem', minWidth: 0 }}>
            {(() => { const t = zmanimForDay?.dayExitSunset || zmanimForDay?.sunset; return t ? (
              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ width: '100%' }} spacing={1}>
                <span>שקיעה ביום המועד</span>
                <Typography sx={{ fontSize: '0.72rem', color: '#9ca3af', flexShrink: 0, fontWeight: 400 }}>{t}</Typography>
              </Stack>
            ) : 'שקיעה ביום המועד'; })()}
          </MenuItem>,
        ]}

        {/* Extra options (candleLighting, havdalah, fastBegins, fastEnds — date-specific calendar events) */}
        {(() => {
          const visibleExtras = extraOptions; // always show all extra options
          if (visibleExtras.length === 0) return null;
          return [
            <ListSubheader key="__extra_hdr" sx={{ fontSize: '0.68rem', lineHeight: '26px', color: '#0c4a6e', bgcolor: '#f0f9ff' }}>
              {showSentinels ? 'זמנים ייחודיים ליום זה' : 'זמני המועד'}
            </ListSubheader>,
            ...visibleExtras.map(opt => {
              const optTime = opt.time || zmanimForDay?.[opt.key];
              return (
                <MenuItem key={`extra_${opt.key}`} value={opt.key} sx={{ fontSize: '0.8rem', fontWeight: 600, color: '#1e3a5f' }}>
                  {optTime ? (
                    <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ width: '100%' }} spacing={1}>
                      <span>{opt.label}</span>
                      <Typography sx={{ fontSize: '0.72rem', color: '#6b7280', flexShrink: 0, fontWeight: 400 }}>{optTime}</Typography>
                    </Stack>
                  ) : opt.label}
                </MenuItem>
              );
            }),
          ];
        })()}

        {/* Standard zmanim groups — flat array (no Fragment) so MUI Select can traverse for display */}
        {orderedGroups.flatMap((group, gi) => [
          <ListSubheader key={`__grp_${gi}`} sx={{ fontSize: '0.68rem', lineHeight: '26px', color: '#9ca3af' }}>
            {group.header}
          </ListSubheader>,
          ...group.defs.map(def => {
            // In day* mode, emit the day* key; in direct mode, emit the direct key
            const menuKey = keyPrefix === 'day' ? (DIRECT_TO_DAY_MAP[def.key] ?? def.key) : def.key;
            return renderOption(def.key, menuKey, def.label, def.primary ? 700 : 400);
          }),
        ])}

        {/* None option */}
        {showNone && (
          <MenuItem value="none" sx={{ fontSize: '0.8rem', color: '#9ca3af', fontStyle: 'italic' }}>
            ללא — אל תציג זמן זה
          </MenuItem>
        )}
      </Select>

      {/* ⓘ Tooltip */}
      {tooltipContent ? (
        <Tooltip title={tooltipContent} arrow placement="left">
          <Box
            sx={{
              cursor: 'help',
              color: '#9ca3af',
              flexShrink: 0,
              display: 'flex',
              alignItems: 'center',
              '&:hover': { color: '#1e3a5f' },
              transition: 'color 0.15s',
            }}
          >
            <Info size={14} />
          </Box>
        </Tooltip>
      ) : (
        <Box sx={{ width: 14, flexShrink: 0 }} />
      )}
    </Stack>
  );
};

export default ZmanSelect;
